import classNames from "classnames";
import Spinner from "components/ui/Spinner";
import {
  MouseEvent,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { trapTabKey } from "utils/a11y";
import { useDialogTracking } from "./analytics";
import { DialogProvider } from "./DialogContext";
import { DefaultDialogHeader } from "./headers";
import styles from "./index.module.scss";

// Every open dialog, oldest first. A dialog opened from another one portals
// later and so paints on top; this is how the ones underneath know they are not
// the dialog being looked at.
const openDialogs: string[] = [];
let overflowBeforeFirst = "";

type DialogProps = {
  open: boolean;
  /**
   * Names the dialog for assistive tech, and is what the header renders. A
   * string, not a node: an accessible name has to be announceable, and all 119
   * call sites already pass one.
   */
  title: string;
  onClose: () => void;
  /**
   * The panel's header. Defaults to DefaultDialogHeader; pass CenteredDialogHeader
   * or BasicDialogHeader for the other shapes, or null for a body that owns the
   * whole composition. A custom one reads the title and close handler from
   * DialogContext rather than taking them as props.
   */
  header?: ReactNode | null;
  // Body inset: "default" (20/22) fits forms, "roomy" (32/48, 24 on mobile) fits
  // celebration and prompt compositions, "none" hands the inset to a body that
  // brings its own.
  bodyPadding?: "default" | "roomy" | "none";
  /** The confirming action. Rightmost on a wide footer, top of the stacked column. */
  mainAction?: ReactNode;
  /** The dismissing action, placed against mainAction. */
  secondaryAction?: ReactNode;
  /**
   * Anything the footer needs beyond the confirm/dismiss pair, anchored to its far
   * left. The one slot the Dialog makes no assumptions about: rendered in the order
   * given, so a caller passing several owns their arrangement.
   */
  extraActions?: ReactNode;
  /**
   * Renders a spinner after the children, for a write the dialog is waiting on.
   * Purely visual — it disables nothing; pair with disabled props on the footer
   * actions where needed.
   */
  loading?: boolean;
  /**
   * How wide the panel is. Omit it only for content that already carries an
   * intrinsic width, and the panel takes what that content asks for, capped to
   * the viewport. Content laid out against its container has none, however
   * fixed it looks: the calendars here are width: 100% and collapse.
   *
   * Everything else names a step, prose and form fields especially — text has
   * no width of its own, so left to itself a dialog comes out as wide as its
   * longest sentence, and a different width in every language.
   */
  size?: "xs" | "sm" | "md" | "lg";
  /**
   * Opts the dialog into product analytics under this id: a stable snake_case
   * name for the dialog itself. Not derived from the title, which is translated
   * and in places interpolates the user's own name.
   */
  analyticsId?: string;
  /** Set false for dialogs with unsaved input — disables overlay-click AND Escape. Defaults to true. */
  closeOnOverlayClick?: boolean;
  /**
   * Extra class on the panel — the styling hook for call-site css (calendar
   * skins, per-dialog layout). Keep panel-level styling here, not in the kit.
   */
  className?: string;
  children: ReactNode;
};

/**
 * The app's dialog shell (rounded panel on a soft scrim): role="dialog",
 * aria-modal, labelled by its title, Escape-to-close, body scroll lock,
 * focus trap, and focus restore to the opener on close.
 *
 * A dialog may open another one and stay mounted behind it: the later one
 * portals after and so paints on top, takes Escape for itself, and the page
 * stays locked until the last of them closes.
 */
export default function Dialog(props: DialogProps) {
  const { open, onClose, closeOnOverlayClick } = props;
  const dialogId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const header = props.header === undefined ? <DefaultDialogHeader /> : props.header;

  const recordClose = useDialogTracking({
    dialog: props.analyticsId,
    open,
  });

  // The chrome's own close, handed to the header through context so a custom
  // header gets the same wiring — and the same attribution.
  const closeFromChrome = useCallback(() => {
    recordClose("close_button");
    onClose();
  }, [recordClose, onClose]);

  // Joins the open stack, which is what makes a dialog opened from another one
  // behave: the page stays locked until the last of them closes, so closing the
  // outer one first cannot unlock the page under the inner one.
  useEffect(() => {
    if (!open) {
      return;
    }
    if (openDialogs.length === 0) {
      overflowBeforeFirst = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    openDialogs.push(dialogId);
    return () => {
      const at = openDialogs.indexOf(dialogId);
      if (at !== -1) {
        openDialogs.splice(at, 1);
      }
      if (openDialogs.length === 0) {
        document.body.style.overflow = overflowBeforeFirst;
      }
    };
  }, [open, dialogId]);

  useEffect(() => {
    if (!open || closeOnOverlayClick === false) {
      return;
    }
    const handleKeyDown = (evt: globalThis.KeyboardEvent) => {
      // Escape is aimed at the dialog on top, and every open dialog hears the
      // keydown, so the ones underneath have to ignore it.
      if (evt.key !== "Escape" || openDialogs.at(-1) !== dialogId) {
        return;
      }
      recordClose("escape");
      onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose, closeOnOverlayClick, recordClose, dialogId]);

  // Move focus into the panel on open; hand it back to the opener on close.
  useEffect(() => {
    if (!open) {
      return;
    }
    const opener = document.activeElement;
    panelRef.current?.focus();
    return () => {
      if (opener instanceof HTMLElement && opener.isConnected) {
        opener.focus();
      }
    };
  }, [open]);

  if (!open) {
    return null;
  }

  const handleOverlayClick = (evt: MouseEvent) => {
    if (evt.target === evt.currentTarget && props.closeOnOverlayClick !== false) {
      recordClose("overlay");
      onClose();
    }
  };

  // Keep Tab cycling inside the panel while the dialog is open.
  const handleTrapKeyDown = (evt: ReactKeyboardEvent) => {
    if (evt.key === "Tab") {
      trapTabKey(panelRef.current, evt);
    }
  };

  return createPortal(
    <DialogProvider title={props.title} onClose={closeFromChrome}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop click-to-close supplements Escape; the overlay is deliberately not in the tab order. */}
      <div className={styles.overlay} onClick={handleOverlayClick}>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the keydown IS the focus trap; role="dialog" containers are the one place a div legitimately listens for Tab. */}
        <div
          ref={panelRef}
          tabIndex={-1}
          className={classNames(
            styles.dialog,
            props.size && styles[props.size],
            props.className
          )}
          role="dialog"
          aria-modal="true"
          aria-label={props.title}
          onKeyDown={handleTrapKeyDown}
        >
          {header}

          <div
            className={classNames(
              styles.body,
              props.bodyPadding === "roomy" && styles.bodyRoomy,
              props.bodyPadding === "none" && styles.bodyNone
            )}
          >
            {props.children}
            {props.loading && <Spinner />}
          </div>

          {(props.mainAction || props.secondaryAction || props.extraActions) && (
            <footer className={styles.foot}>
              {props.extraActions && (
                <div className={styles.footExtra}>{props.extraActions}</div>
              )}
              {props.secondaryAction}
              {props.mainAction}
            </footer>
          )}
        </div>
      </div>
    </DialogProvider>,
    document.body
  );
}

type StickyActionsProps = {
  className?: string;
  children: ReactNode;
};

/**
 * Pins primary actions to the bottom of the dialog body's scrollport, so tall
 * body content scrolls under them instead of pushing them out of view. Use this
 * for wizard-style dialogs whose CTA lives in the scrolling body rather than
 * the Dialog `footer` prop.
 *
 * Place as a direct child of the dialog body, or of a container spanning the
 * body's full scroll height: position:sticky cannot escape its parent.
 */
export function StickyActions(props: StickyActionsProps) {
  return (
    <div className={classNames(styles.stickyActions, props.className)}>
      {props.children}
    </div>
  );
}
